import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { config } from "../pages/Test/config";

// 統一的認證服務
export class AuthService {
  // 檢查 token 是否過期
  static async isTokenValid() {
    try {
      const authSession = await fetchAuthSession();
      if (authSession.tokens && authSession.tokens.accessToken) {
        const accessToken = authSession.tokens.accessToken;
        const tokenExpiry = accessToken.payload.exp * 1000;
        const currentTime = Date.now();

        console.log("AuthService: Token 檢查", {
          expiry: new Date(tokenExpiry),
          current: new Date(currentTime),
          isValid: currentTime < tokenExpiry,
          remainingMinutes: Math.floor(
            (tokenExpiry - currentTime) / (1000 * 60)
          ),
        });

        return currentTime < tokenExpiry;
      }
      return false;
    } catch (error) {
      // 如果是 Identity Pool 配置問題，記錄但不拋出錯誤
      if (error.name === "InvalidIdentityPoolConfigurationException") {
        console.log("AuthService: Identity Pool 配置問題，跳過 token 檢查");
        console.log("AuthService: 這不影響用戶登入功能，僅影響 AWS 服務訪問");
        // 返回 true 表示認證有效（基於用戶登入狀態）
        return true;
      }

      console.log("AuthService: Token 檢查失敗", error);
      return false;
    }
  }

  // 檢查用戶是否已登入（輕量級檢查）
  static async isUserLoggedIn() {
    try {
      const currentUser = await getCurrentUser();
      return !!currentUser;
    } catch (error) {
      console.log("AuthService: 用戶登入檢查失敗", error);
      return false;
    }
  }

  // 混合認證檢查（推薦使用）
  static async isAuthenticatedHybrid() {
    try {
      // 步驟1: 輕量級檢查（快速且穩定）
      const isLoggedIn = await this.isUserLoggedIn();
      if (!isLoggedIn) {
        console.log("AuthService: 混合檢查 - 用戶未登入");
        return false;
      }

      // 步驟2: 嘗試完整檢查（如果失敗則回退到輕量級）
      try {
        const isTokenValid = await this.isTokenValid();
        console.log("AuthService: 混合檢查 - Token 檢查成功:", isTokenValid);
        return isTokenValid;
      } catch (tokenError) {
        console.log(
          "AuthService: 混合檢查 - Token 檢查失敗，回退到輕量級檢查:",
          tokenError.message
        );
        // 如果 token 檢查失敗，但用戶已登入，則允許訪問
        // 但記錄警告
        console.warn("AuthService: 警告 - 無法驗證 token 有效性，但用戶已登入");
        return true;
      }
    } catch (error) {
      console.log("AuthService: 混合檢查失敗", error);
      return false;
    }
  }

  // 檢查是否需要重新登入（用於定期檢查）
  static async needsReauthentication() {
    try {
      // 首先檢查用戶是否登入
      const isLoggedIn = await this.isUserLoggedIn();
      if (!isLoggedIn) {
        return true; // 需要重新登入
      }

      // 嘗試檢查 token 有效性
      try {
        const isTokenValid = await this.isTokenValid();
        if (!isTokenValid) {
          console.log("AuthService: Token 已過期，需要重新登入");
          return true;
        }
        return false; // 不需要重新登入
      } catch (tokenError) {
        console.log(
          "AuthService: 無法檢查 token 有效性，假設需要重新登入:",
          tokenError.message
        );
        return true; // 保守策略：無法檢查時要求重新登入
      }
    } catch (error) {
      console.log("AuthService: 檢查重新登入需求失敗", error);
      return true; // 出錯時要求重新登入
    }
  }

  // 獲取 token 過期時間
  static async getTokenExpiry() {
    try {
      const authSession = await fetchAuthSession();
      if (authSession.tokens && authSession.tokens.accessToken) {
        const accessToken = authSession.tokens.accessToken;
        return new Date(accessToken.payload.exp * 1000);
      }
      return null;
    } catch (error) {
      console.log("AuthService: 獲取 token 過期時間失敗", error);
      return null;
    }
  }

  // 獲取剩餘有效時間（分鐘）
  static async getRemainingMinutes() {
    try {
      const expiry = await this.getTokenExpiry();
      if (expiry) {
        const remaining = Math.floor(
          (expiry.getTime() - Date.now()) / (1000 * 60)
        );
        return Math.max(0, remaining);
      }
      return 0;
    } catch (error) {
      console.log("AuthService: 獲取剩餘時間失敗", error);
      return 0;
    }
  }

  // 打印 ID Token 詳細信息（用於調試）
  static async printIdTokenDetails() {
    try {
      console.log("=== ID Token 詳細信息 ===");

      // 首先嘗試獲取基本用戶信息（User Pool 認證）
      let currentUser;
      try {
        currentUser = await getCurrentUser();
        console.log("✅ Cognito User Pool 認證正常");
        console.log("當前用戶:", currentUser);
      } catch (userError) {
        console.log("❌ Cognito User Pool 認證失敗:", userError.message);
        return;
      }

      // 解釋為什麼需要 fetchAuthSession
      console.log("📝 說明：為什麼需要 fetchAuthSession？");
      console.log(
        "📝 1. localStorage 中的 userData 是應用程序數據，不包含 JWT tokens"
      );
      console.log(
        "📝 2. JWT tokens (ID Token, Access Token) 由 AWS Amplify 內部管理"
      );
      console.log("📝 3. fetchAuthSession() 是獲取這些 tokens 的標準方法");
      console.log("📝 4. 但 fetchAuthSession() 依賴於 Identity Pool 配置");

      // 顯示應用程序存儲的數據
      this.displayAppUserData();

      // 嘗試獲取 AWS Amplify 的 tokens
      let authSession;
      try {
        authSession = await fetchAuthSession();
        console.log("✅ 成功獲取 AWS Amplify tokens");

        if (authSession.tokens) {
          this.displayTokenDetails(authSession.tokens, authSession);
        } else {
          console.log("❌ fetchAuthSession 返回了空的 tokens");
        }
      } catch (sessionError) {
        console.log("⚠️ fetchAuthSession 失敗，嘗試其他方法...");
        console.log("錯誤類型:", sessionError.name);
        console.log("錯誤信息:", sessionError.message);

        // 嘗試從瀏覽器存儲中直接讀取
        this.tryGetTokensFromStorage();
      }
    } catch (error) {
      console.error("❌ 打印 ID Token 詳細信息時發生錯誤:", error);
      console.log("錯誤詳情:", {
        name: error.name,
        message: error.message,
        stack: error.stack,
      });
    }
  }

  // 顯示應用程序存儲的用戶數據
  static displayAppUserData() {
    console.log("📝 應用程序用戶數據 (Context):");
    console.log("📝 注意：用戶數據現在通過 React Context 管理");
    console.log("📝 JWT tokens 由 AWS Amplify 內部管理");
  }

  // 顯示 token 詳細信息
  static displayTokenDetails(tokens, authSession) {
    console.log("📝 AWS Amplify 管理的 JWT Tokens:");

    // 檢查 ID Token
    if (tokens.idToken) {
      const idToken = tokens.idToken;
      console.log("✅ ID Token 存在");
      console.log("ID Token 完整值:", idToken.toString());
      console.log("ID Token 過期時間:", new Date(idToken.payload.exp * 1000));
      console.log("ID Token 發行時間:", new Date(idToken.payload.iat * 1000));
      console.log("ID Token 發行者:", idToken.payload.iss);
      console.log("ID Token 受眾:", idToken.payload.aud);
      console.log("ID Token 主題:", idToken.payload.sub);

      // 檢查自定義聲明
      const customClaims = {};
      Object.keys(idToken.payload).forEach((key) => {
        if (key.startsWith("custom:")) {
          customClaims[key] = idToken.payload[key];
        }
      });

      if (Object.keys(customClaims).length > 0) {
        console.log("ID Token 自定義聲明:", customClaims);
      } else {
        console.log("ID Token 沒有自定義聲明");
      }

      // 檢查是否過期
      const currentTime = Date.now() / 1000;
      const isExpired = idToken.payload.exp < currentTime;
      console.log("ID Token 是否過期:", isExpired ? "是" : "否");

      if (!isExpired) {
        const remainingSeconds = idToken.payload.exp - currentTime;
        const remainingMinutes = Math.floor(remainingSeconds / 60);
        console.log(
          "ID Token 剩餘有效時間:",
          `${remainingMinutes} 分鐘 (${remainingSeconds} 秒)`
        );
      }
    } else {
      console.log("❌ 沒有找到 ID Token");
    }

    // 檢查 Access Token
    if (tokens.accessToken) {
      const accessToken = tokens.accessToken;
      console.log("✅ Access Token 存在");
      console.log(
        "Access Token 過期時間:",
        new Date(accessToken.payload.exp * 1000)
      );
      console.log(
        "Access Token 是否過期:",
        accessToken.payload.exp < Date.now() / 1000 ? "是" : "否"
      );
    } else {
      console.log("❌ 沒有找到 Access Token");
    }

    // 檢查 Refresh Token
    if (tokens.refreshToken) {
      console.log("✅ Refresh Token 存在");
    } else {
      console.log("❌ 沒有找到 Refresh Token");
    }

    // 顯示認證會話信息
    if (authSession) {
      console.log("=== 認證會話信息 ===");
      console.log("用戶子:", authSession.userSub);
      console.log("身份 ID:", authSession.identityId);
      console.log("憑證:", authSession.credentials);
    }
  }

  // 嘗試從瀏覽器存儲中直接讀取 tokens
  static tryGetTokensFromStorage() {
    console.log("🔍 嘗試從瀏覽器存儲中直接讀取 AWS Amplify 數據...");

    try {
      // 檢查 localStorage 中的 AWS Amplify 相關數據
      const amplifyKeys = Object.keys(localStorage).filter(
        (key) =>
          key.includes("amplify") ||
          key.includes("cognito") ||
          key.includes("aws")
      );

      if (amplifyKeys.length > 0) {
        console.log("📝 找到 AWS Amplify 相關的 localStorage 鍵:", amplifyKeys);
        amplifyKeys.forEach((key) => {
          try {
            const value = localStorage.getItem(key);
            console.log(
              `📝 ${key}:`,
              value ? value.substring(0, 100) + "..." : "null"
            );
          } catch (e) {
            console.log(`📝 ${key}: 無法讀取`);
          }
        });
      } else {
        console.log("📝 沒有找到 AWS Amplify 相關的 localStorage 鍵");
        console.log("📝 AWS Amplify 可能使用 IndexedDB 存儲 tokens");
      }
    } catch (error) {
      console.log("❌ 無法從瀏覽器存儲讀取:", error.message);
    }
  }

  // 純 User Pool token 檢查（不觸發 Identity Pool）
  static async checkUserPoolTokensOnly() {
    try {
      // 直接從瀏覽器存儲讀取 User Pool tokens
      // 這些 tokens 通常在登入時已經存儲在瀏覽器中

      // 檢查是否有 Cognito 相關的存儲
      const cognitoKeys = Object.keys(localStorage).filter(
        (key) =>
          key.includes("CognitoIdentityServiceProvider") ||
          key.includes("cognito")
      );

      if (cognitoKeys.length > 0) {
        console.log("AuthService: 找到 Cognito 存儲鍵:", cognitoKeys);

        // 嘗試從這些存儲中讀取 token 信息
        for (const key of cognitoKeys) {
          try {
            const value = localStorage.getItem(key);
            if (value && value.includes("accessToken")) {
              console.log("AuthService: 找到 access token 存儲");
              // 這裡可以解析 token 並檢查過期時間
              return true;
            }
          } catch (e) {
            console.log(`AuthService: 無法讀取 ${key}:`, e.message);
          }
        }
      }

      return false;
    } catch (error) {
      console.log("AuthService: 純 User Pool 檢查失敗:", error);
      return false;
    }
  }

  // 超輕量級認證檢查（已棄用，改為使用 Context）
  static async isAuthenticatedUltraLightweight() {
    try {
      // 用戶數據現在通過 Context 管理，不依賴 localStorage
      console.log("AuthService: 超輕量級檢查已棄用，請使用 Context");
      return false;
    } catch (error) {
      console.log("AuthService: 超輕量級檢查失敗:", error);
      return false;
    }
  }

  // 檢查是否有 refresh token
  static hasRefreshToken() {
    try {
      const keys = Object.keys(localStorage);
      const refreshTokenKeys = keys.filter(
        (key) =>
          key.includes("refreshToken") ||
          key.includes("refresh") ||
          (key.includes("token") &&
            !key.includes("access") &&
            !key.includes("id"))
      );

      console.log("🔍 Refresh Token 檢查結果:", {
        totalKeys: keys.length,
        refreshTokenKeys: refreshTokenKeys,
        hasRefreshToken: refreshTokenKeys.length > 0,
      });

      return refreshTokenKeys.length > 0;
    } catch (error) {
      console.log("AuthService: 檢查 refresh token 失敗:", error);
      return false;
    }
  }

  // 獲取 Cognito token 詳細信息（用於顯示在 Tokens 頁面）
  static async getCognitoTokenDetails() {
    try {
      const keys = Object.keys(localStorage);
      console.log("🔍 所有 localStorage 鍵:", keys);

      // 更寬鬆的搜尋條件，包含所有可能的 Cognito 相關鍵
      const cognitoKeys = keys.filter(
        (key) =>
          (key.includes("CognitoIdentityServiceProvider") &&
            (key.includes("accessToken") ||
              key.includes("idToken") ||
              key.includes("refreshToken"))) ||
          key.includes("cognito") ||
          key.includes("refresh") ||
          key.includes("token")
      );

      // 額外檢查是否有其他可能的 Refresh Token 鍵名
      const allCognitoKeys = keys.filter(
        (key) =>
          key.includes("CognitoIdentityServiceProvider") ||
          key.includes("cognito") ||
          key.includes("refresh") ||
          key.includes("token")
      );

      console.log("🔍 所有可能的 Cognito 相關鍵:", allCognitoKeys);

      console.log("🔍 找到的 Cognito 相關鍵:", cognitoKeys);

      if (cognitoKeys.length === 0) {
        console.log("AuthService: 未找到 Cognito token");
        return null;
      }

      const tokenDetails = [];

      cognitoKeys.forEach((key) => {
        try {
          const tokenValue = localStorage.getItem(key);
          console.log(
            `🔍 處理鍵: ${key}, 值長度: ${tokenValue ? tokenValue.length : 0}`
          );

          // 詳細調試：檢查鍵名是否包含特定關鍵字
          console.log(`🔍 鍵名分析:`, {
            key: key,
            includesRefreshToken: key.includes("refreshToken"),
            includesRefresh: key.includes("refresh"),
            includesAccessToken: key.includes("accessToken"),
            includesIdToken: key.includes("idToken"),
            includesToken: key.includes("token"),
            includesCognito: key.includes("Cognito"),
          });

          if (tokenValue) {
            // 更準確的 token 類型識別
            let tokenType;
            if (key.includes("accessToken")) {
              tokenType = "Access Token";
            } else if (key.includes("idToken")) {
              tokenType = "ID Token";
            } else if (
              key.includes("refreshToken") ||
              key.includes("refresh")
            ) {
              tokenType = "Refresh Token";
            } else if (
              key.includes("token") &&
              !key.includes("access") &&
              !key.includes("id")
            ) {
              // 如果鍵名包含 "token" 但不包含 "access" 或 "id"，可能是 refresh token
              tokenType = "Refresh Token";
            } else {
              tokenType = "Unknown Token";
            }

            console.log(`🔍 識別為: ${tokenType}`);

            // 解析 JWT token 獲取期限
            try {
              let payload = null;

              // Refresh Token 可能是加密的，無法直接解析為 JWT
              if (tokenType === "Refresh Token") {
                console.log(`🔍 Refresh Token 處理開始...`);

                // 嘗試解析 Refresh Token（雖然通常會失敗）
                try {
                  const parsedPayload = this.parseJwt(tokenValue);
                  if (parsedPayload && parsedPayload.exp) {
                    console.log(`🔍 Refresh Token 解析成功，使用真實數據`);
                    payload = parsedPayload;
                  } else {
                    throw new Error("無法解析 Refresh Token");
                  }
                } catch (parseError) {
                  console.log(
                    `🔍 Refresh Token 解析失敗，使用模擬數據:`,
                    parseError.message
                  );

                  // 嘗試從 localStorage 中獲取更多真實信息
                  const allKeys = Object.keys(localStorage);
                  const signInDetailsKey = allKeys.find((key) =>
                    key.includes("signInDetails")
                  );
                  let signInTime = null;

                  if (signInDetailsKey) {
                    try {
                      const signInDetails = JSON.parse(
                        localStorage.getItem(signInDetailsKey)
                      );
                      console.log(`🔍 找到登入詳情:`, signInDetails);
                      // 可以從這裡提取更多真實信息
                    } catch (e) {
                      console.log(`🔍 解析登入詳情失敗:`, e.message);
                    }
                  }

                  // 為 Refresh Token 創建一個模擬的 payload
                  const now = Math.floor(Date.now() / 1000);
                  // AWS Cognito Refresh Token 通常有 30 天到 1 年的有效期
                  // 我們假設它是在 7 天前發行的，還有 23 天有效期
                  payload = {
                    iat: now - 7 * 24 * 60 * 60, // 7天前發行
                    exp: now + 23 * 24 * 60 * 60, // 23天後過期（總共30天）
                    jti:
                      "refresh_token_" +
                      Math.random().toString(36).substr(2, 9),
                    iss: "cognito-idp.ap-northeast-1.amazonaws.com/ap-northeast-1_bjFVkWW0I",
                    sub: "refresh_token_user",
                    aud: "26pobecup4mv1prm7ranb7mkuh", // 從 localStorage 鍵名中提取的 client ID
                  };
                  console.log(
                    `🔍 Refresh Token 模擬 payload 創建完成:`,
                    payload
                  );
                }
              } else {
                payload = this.parseJwt(tokenValue);
              }

              console.log(`🔍 ${tokenType} payload:`, payload);

              // Refresh Token 可能沒有 exp 字段，需要特殊處理
              if (payload) {
                let expiryDate,
                  remainingMs,
                  remainingMinutes,
                  remainingHours,
                  remainingDays;
                let status = "";
                let statusColor = "#ffffff";

                if (payload.exp) {
                  // 有過期時間的 token
                  expiryDate = new Date(payload.exp * 1000);
                  const now = new Date();
                  remainingMs = expiryDate.getTime() - now.getTime();
                  remainingMinutes = Math.floor(remainingMs / (1000 * 60));
                  remainingHours = Math.floor(remainingMinutes / 60);
                  remainingDays = Math.floor(remainingHours / 24);

                  if (remainingMs <= 0) {
                    status = "⚠️ TOKEN 已過期！";
                    statusColor = "#e74c3c";
                  } else if (remainingMinutes < 60) {
                    status = "🟡 TOKEN 即將過期（1小時內）";
                    statusColor = "#f39c12";
                  } else if (remainingHours < 24) {
                    status = "🟠 TOKEN 今天過期";
                    statusColor = "#e67e22";
                  } else {
                    status = "🟢 TOKEN 有效期正常";
                    statusColor = "#27ae60";
                  }
                } else {
                  // 沒有過期時間的 token（通常是 Refresh Token）
                  expiryDate = new Date(
                    payload.iat * 1000 + 30 * 24 * 60 * 60 * 1000
                  ); // 假設30天
                  const now = new Date();
                  remainingMs = expiryDate.getTime() - now.getTime();
                  remainingMinutes = Math.floor(remainingMs / (1000 * 60));
                  remainingHours = Math.floor(remainingMinutes / 60);
                  remainingDays = Math.floor(remainingHours / 24);

                  status = "🔄 REFRESH TOKEN（長期有效）";
                  statusColor = "#3498db";
                }

                // 為 ID Token 添加額外的詳細信息
                if (tokenType === "ID Token") {
                  tokenDetails.push({
                    type: tokenType,
                    expiry: expiryDate.toLocaleString(),
                    remaining: `${remainingDays}天 ${remainingHours % 24}小時 ${
                      remainingMinutes % 60
                    }分鐘`,
                    status: status,
                    statusColor: statusColor,
                    // 基本信息
                    issuedAt: payload.iat
                      ? new Date(payload.iat * 1000).toLocaleString()
                      : "未知",
                    tokenId: payload.jti || "未知",
                    issuer: payload.iss || "未知",
                    // ID Token 特有信息
                    subject: payload.sub || "未知",
                    audience: payload.aud || "未知",
                    authTime: payload.auth_time
                      ? new Date(payload.auth_time * 1000).toLocaleString()
                      : "未知",
                    // 用戶信息
                    username:
                      payload["cognito:username"] || payload.username || "未知",
                    email: payload.email || "未知",
                    emailVerified: payload.email_verified ? "是" : "否",
                    // 自定義屬性
                    customAttributes: this.extractCustomAttributes(payload),
                    // 完整 payload（用於調試）
                    fullPayload: payload,
                  });
                } else if (tokenType === "Refresh Token") {
                  // Refresh Token 特殊處理
                  console.log("🔍 處理 Refresh Token:", payload);

                  // 檢查是否為模擬數據
                  const isSimulated =
                    payload.jti && payload.jti.startsWith("refresh_token_");

                  tokenDetails.push({
                    type: tokenType,
                    expiry: expiryDate.toLocaleString(),
                    remaining: `${remainingDays}天 ${remainingHours % 24}小時 ${
                      remainingMinutes % 60
                    }分鐘`,
                    status: isSimulated
                      ? "🔄 REFRESH TOKEN（模擬數據）"
                      : status,
                    statusColor: isSimulated ? "#9b59b6" : statusColor, // 紫色表示模擬數據
                    issuedAt: payload.iat
                      ? new Date(payload.iat * 1000).toLocaleString()
                      : "未知",
                    tokenId: payload.jti || "未知",
                    issuer: payload.iss || "未知",
                    // Refresh Token 特有信息
                    subject: payload.sub || "未知",
                    audience: payload.aud || "未知",
                    // 完整 payload（用於調試）
                    fullPayload: payload,
                    // 添加原始 token 值的前50個字符（用於調試）
                    tokenPreview: tokenValue.substring(0, 50) + "...",
                    // 標記是否為模擬數據
                    isSimulated: isSimulated,
                  });
                  console.log("✅ Refresh Token 已添加到 tokenDetails");
                  console.log(
                    "✅ 當前 tokenDetails 長度:",
                    tokenDetails.length
                  );
                  console.log("✅ 是否為模擬數據:", isSimulated);
                } else {
                  // Access Token 保持原有信息
                  tokenDetails.push({
                    type: tokenType,
                    expiry: expiryDate.toLocaleString(),
                    remaining: `${remainingDays}天 ${remainingHours % 24}小時 ${
                      remainingMinutes % 60
                    }分鐘`,
                    status: status,
                    statusColor: statusColor,
                    issuedAt: payload.iat
                      ? new Date(payload.iat * 1000).toLocaleString()
                      : "未知",
                    tokenId: payload.jti || "未知",
                    issuer: payload.iss || "未知",
                  });
                }
              }
            } catch (parseError) {
              console.log(`解析 ${key} 失敗:`, parseError.message);
            }
          }
        } catch (error) {
          console.log(`讀取 ${key} 失敗:`, error.message);
        }
      });

      console.log("🔍 最終返回的 tokenDetails:", tokenDetails);
      console.log("🔍 處理的鍵數量:", cognitoKeys.length);
      console.log("🔍 成功處理的 token 數量:", tokenDetails.length);

      // 檢查是否有鍵被跳過
      const processedKeys = tokenDetails.map((token) => {
        // 從 token 信息中推斷原始鍵名
        if (token.type === "Access Token") return "accessToken";
        if (token.type === "ID Token") return "idToken";
        if (token.type === "Refresh Token") return "refreshToken";
        return "unknown";
      });
      console.log("🔍 已處理的 token 類型:", processedKeys);

      return tokenDetails;
    } catch (error) {
      console.log("AuthService: 獲取 Cognito token 詳細信息失敗:", error);
      return null;
    }
  }

  // 解析 JWT token 的輔助方法
  static parseJwt(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.log("AuthService: 解析 JWT 失敗:", error);
      return null;
    }
  }

  // 提取自定義屬性的輔助方法
  static extractCustomAttributes(payload) {
    const customAttrs = {};

    // 遍歷所有 payload 屬性，找出自定義屬性
    Object.keys(payload).forEach((key) => {
      // 跳過標準 JWT 屬性
      const standardJwtClaims = [
        "iss",
        "sub",
        "aud",
        "exp",
        "nbf",
        "iat",
        "jti",
        "auth_time",
        "email",
        "email_verified",
        "username",
        "cognito:username",
      ];

      if (!standardJwtClaims.includes(key)) {
        customAttrs[key] = payload[key];
      }
    });

    return customAttrs;
  }
}
